import { useSnackbar } from 'notistack';
import { useContext } from 'react';
import { useMutation } from 'react-query';
import { CLIENT_ID, REJECTION_MASS_ACTION_LAMBDA } from 'shared/constants';
import { useService } from 'shared/providers';
import { ExecuteLambdaResponse } from 'shared/services/lambda-service';
import { TableDataContext } from 'shared/types';
import { generateCollectionFilter } from 'shared/utility/dataGrid';
import { ApprovalDashboardData } from 'types/backend';
import { ApprovalMassActionLambdaInputs, ApprovalMassActionLambdaOutputs } from 'types/lambda';
import { useI2PEnvironment } from '../useI2PEnvironment';

interface useRejectionMassActionLambdaMutationProps {
    gridContext: React.Context<TableDataContext<ApprovalDashboardData>>;
    selectedRowIds: number[];
    isAllRowsSelected: boolean;
    onSuccess?: () => void;
    isRequestorOnly: boolean;
}

export function useRejectionMassActionLambdaMutation({
    gridContext,
    selectedRowIds,
    isAllRowsSelected,
    onSuccess,
    isRequestorOnly,
}: useRejectionMassActionLambdaMutationProps) {
    const { lambdaService } = useService();
    const { enqueueSnackbar } = useSnackbar();
    const { isDev, isProd } = useI2PEnvironment();
    const { filtersApplied, refreshData, massActionModal } = useContext(gridContext);

    return useMutation<
        ExecuteLambdaResponse<ApprovalMassActionLambdaOutputs<ApprovalDashboardData>>,
        Error,
        string | undefined | null
    >(
        [REJECTION_MASS_ACTION_LAMBDA],
        async (comment) => {
            return await lambdaService.executeLambda<
                ApprovalMassActionLambdaInputs,
                ApprovalMassActionLambdaOutputs<ApprovalDashboardData>
            >({
                clientId: CLIENT_ID,
                lambdaId: REJECTION_MASS_ACTION_LAMBDA,
                lambdaInputs: {
                    massActionImpactConfig: {
                        collectionFilter: filtersApplied ? generateCollectionFilter(filtersApplied) : undefined,
                        isAllRowsSelected: isAllRowsSelected,
                        selectedRowIds: selectedRowIds,
                    },
                    approvalDashboardBaseParams: {
                        isRequestorOnly: isRequestorOnly,
                    },
                    isApproved: false,
                    comment: comment || '',
                    shouldSendEmail: isProd,
                    envUrl: `${window.location.origin}/approvals`,
                },
            });
        },
        {
            onSuccess: (_data, _variables, _context) => {
                enqueueSnackbar(`Rejection mass action successful`, {
                    variant: 'success',
                });
                onSuccess?.();
                refreshData();
                massActionModal.handleMassActionModalClose();
            },
            onError: (error, _variables, _context) => {
                enqueueSnackbar('Rejection mass action failed', { variant: 'error' });
                if (isDev) console.error(error);
            },
        }
    );
}
